/** Type-safe localStorage get with JSON parse and validation */
export function getLocalStorageItem<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    // Basic type check: if fallback is an array, parsed must be an array
    if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
    return parsed as T;
  } catch {
    return fallback;
  }
}

/** Type-safe localStorage set with JSON stringify + cloud sync */
export function setLocalStorageItem<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
    // Notify sync service if this is a syncable key (lazy import to avoid circular deps)
    notifySyncIfNeeded(key);
  } catch {
    // Storage full or unavailable — fail silently
  }
}

/** Lazily notify the sync service when a syncable key is written */
function notifySyncIfNeeded(key: string): void {
  // Known sync keys — must match SYNC_KEYS in syncService.ts
  const SYNCABLE = new Set([
    'user_goals', 'daily_checkins', 'timer_sessions', 'midday_pulses',
    'growth_moments', 'gamification_state', 'coach_messages', 'coach_ai_usage',
    'notification_settings', 'learner_profile', 'survey_result', 'rich_persona_profile',
  ]);
  if (!SYNCABLE.has(key)) return;
  // Dynamic import to avoid circular dependency and keep localStorage util lightweight
  import('@/shared/services/syncService').then(({ markDirty }) => {
    markDirty(key as Parameters<typeof markDirty>[0]);
  }).catch(() => {
    // Sync service not available (e.g. during SSR) — ignore
  });
}
